import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Pagination,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import LoadingCard from "components/LoadingCard/LoadingCard";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { LANGUAGES } from "pages/MarketingPages/languages";
import CategoriesModal from "./CategoriesModal";
import PostEditor from "./PostEditor";
import { deletePost, errorMessage, fetchCategories, fetchPosts } from "./postsApi";

const { Title, Text } = Typography;


const STATUSES = [
  { label: "Всички", value: "" },
  { label: "Публикувани", value: "published" },
  { label: "Чернови", value: "draft" },
];

const PAGE_SIZE = 20;

/**
 * Blog posts for the marketing site.
 *
 * Replaces the two legacy `Blogs` / `BlogsBg` pages: posts are one document per
 * language paired by `translationGroup`, so one screen with a language switch
 * replaces two near-identical screens, and a post's translations are visible
 * from either side.
 */
export default function Posts() {
  const [lang, setLang] = useState("bg");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isManagingCategories, setIsManagingCategories] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchPosts({
        lang,
        status,
        category,
        search,
        page,
        limit: PAGE_SIZE,
      });
      setPosts(data.posts || []);
      setTotal(data.total || 0);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [lang, status, category, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Categories are per language and change rarely — reloaded only when the
  // language changes or the category manager edits them.
  const loadCategories = useCallback(async () => {
    try {
      setCategories(await fetchCategories(lang));
    } catch (error) {
      message.error(`Неуспешно зареждане на категориите: ${errorMessage(error)}`);
    }
  }, [lang]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const switchLanguage = (next) => {
    setLang(next);
    // A category id belongs to one language, so it cannot survive the switch.
    setCategory(undefined);
    setPage(1);
  };

  const confirmDelete = (post) => {
    Modal.confirm({
      title: "Сигурен ли си, че искаш да изтриеш тази статия?",
      content: `„${post.title}“ ще бъде изтрита. Това действие не може да бъде отменено.`,
      okText: "Изтрий",
      cancelText: "Отказ",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deletePost(post._id);
          message.success("Изтрита");
          load();
        } catch (error) {
          message.error(`Неуспешно изтриване: ${errorMessage(error)}`);
        }
      },
    });
  };

  if (editing) {
    return (
      <PostEditor
        postId={editing.postId}
        lang={editing.lang || lang}
        translationGroup={editing.translationGroup}
        onBack={() => {
          setEditing(null);
          load();
        }}
        onSaved={(saved) =>
          // After creating, stay in the editor but switch it to edit mode, so a
          // second save updates the post instead of creating another one.
          setEditing((current) => (current?.postId ? current : { ...current, postId: saved._id }))
        }
      />
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 760, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Статии
        </Title>
        <Text type="secondary">
          Статиите за публичния сайт. Всеки език е отделна статия, свързана с преводите си.
        </Text>
      </Space>

      <Segmented
        options={LANGUAGES}
        value={lang}
        onChange={switchLanguage}
        block
        style={{ marginBottom: 12 }}
      />

      <Space wrap style={{ width: "100%", marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={CONTROL}
          onClick={() => setEditing({ lang })}
        >
          Нова статия
        </Button>
        <Button style={CONTROL} onClick={() => setIsManagingCategories(true)}>
          Категории
        </Button>
      </Space>

      <Space wrap style={{ width: "100%", marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Търси по заглавие"
          defaultValue={search}
          onSearch={(value) => {
            setSearch(value);
            setPage(1);
          }}
          style={{ width: 220, ...CONTROL }}
        />
        <Select
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={STATUSES}
          style={{ width: 130 }}
        />
        <Select
          allowClear
          value={category}
          onChange={(value) => {
            setCategory(value);
            setPage(1);
          }}
          placeholder="Всички категории"
          style={{ width: 180 }}
          options={categories.map((item) => ({ value: item._id, label: item.name }))}
        />
      </Space>

      {isLoading && <LoadingCard />}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на статиите"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <>
          <List
            bordered
            dataSource={posts}
            locale={{
              emptyText: <Empty description="Все още няма статии" />,
            }}
            renderItem={(post) => (
              <List.Item
                style={{ minHeight: 64, flexWrap: "wrap", gap: 8 }}
                actions={[
                  <Button
                    key="edit"
                    type="link"
                    onClick={() => setEditing({ postId: post._id, lang })}
                  >
                    Редактирай
                  </Button>,
                  <Button key="delete" type="link" danger onClick={() => confirmDelete(post)}>
                    Изтрий
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap size={8}>
                      <span>{post.title}</span>
                      <Tag color={post.status === "published" ? "green" : "default"}>
                        {post.status === "published" ? "Публикувана" : "Чернова"}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space wrap size={8}>
                      <Text type="secondary">/{post.slug}</Text>
                      {post.publishedAt && (
                        <Text type="secondary">{dayjs(post.publishedAt).format("DD.MM.YYYY")}</Text>
                      )}
                      {(post.categories || []).map((item) => (
                        <Tag key={item._id}>{item.name}</Tag>
                      ))}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />

          {total > PAGE_SIZE && (
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              style={{ marginTop: 16, textAlign: "center" }}
            />
          )}
        </>
      )}

      {isManagingCategories && (
        <CategoriesModal
          lang={lang}
          categories={categories}
          onClose={() => setIsManagingCategories(false)}
          onChanged={() => {
            loadCategories();
            load();
          }}
        />
      )}
    </div>
  );
}
